import { Component, ElementRef, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonService } from 'src/app/shared/services/common.service';
import { BatteriesSettingsStepValue, BatteryUtilizationSettings, ChargingType, SiteTemperatureUnit, UtilizationSettingType } from '../site-utilization-settings.interface';
import { SitesService } from 'src/app/sites/sites.service';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { Subscription } from 'rxjs';
import { DeviceService } from '../../../device/device.service';
import { DeviceTypeService } from '../../../device/device-type.service';

@Component({
	selector: 'app-battery-utilization-settings',
	templateUrl: './battery-utilization-settings.component.html',
	styleUrls: ['./battery-utilization-settings.component.css']
})

export class BatteryUtilizationSettingsComponent implements OnInit, OnDestroy {
	batteryUtilizationSettings: BatteryUtilizationSettings = {
		min_daily_battery_operating_minutes: 30,
		min_daily_battery_utilization: 30,
		min_battery_energy_utilization: 30,
		min_battery_ah_return: 100,
		max_battery_low_water_level_days: 7,
		max_missed_weekly_wq: 2,
		weak_cell_voltage_limit: 1.7,
		no_of_inuse_cycles_with_vpc_less_weak_voltage_limit: 5,
		deep_discharge_voltage_limit: 1.9,
		no_of_inuse_cycles_with_vpc_less_deep_discharge_limit: 5,
		min_finish_charge_voltage_limit: 2.5,
		no_of_charge_cycles_with_vpc_less_min_finish_charge_voltage_limit: 5,
		min_charge_event_duration: 60,
		geofence_distance: 1000,
		low_temperature_limit: 40,
		maximum_low_temperature_days: 1,
		high_temperature_limit: 130,
		maximum_high_temperature_days: 1,
		min_daily_battery_soc: 30,
		min_daily_plug_ins: 1,
		min_daily_plug_time: 10,
		temperature_unit: 0,
		charging_type: 0
	};

	disableButton: boolean = false;
	siteId: number;
	selectedDays: number[] = [0, 1, 2, 3, 4]; // Days Monday TO Friday ==> checked By Default
	workingDays = this.commonService.workingDays
	settingType: UtilizationSettingType = UtilizationSettingType.Battery;
	BatteriesSettingsStepValue = BatteriesSettingsStepValue;
	subSite: Subscription = new Subscription();
	hasLithiumDevices: boolean = false; // Flag to track if site has Lithium BATTlink devices

	ChargingType = ChargingType;
	TemperatureUnit = SiteTemperatureUnit;

	chargingType: ChargingType = ChargingType.CONVENTIONAL;
	temperatureUnit: SiteTemperatureUnit = SiteTemperatureUnit.FAHRENHEIT;
	highTemp: number;
	lowTemp: number;

	constructor(
		private siteService: SitesService,
		private commonService: CommonService,
		private elementRef: ElementRef,
		private render: Renderer2,
		private route: ActivatedRoute,
		private notificationService: NotificationMessageService,
		private deviceService: DeviceService,
		private deviceTypeService: DeviceTypeService,
	) {}

	ngOnInit() {
		this.siteId = Number(this.route.parent.snapshot.params.siteId);
		this.subSite = this.siteService.siteUtilizationSettingsData.subscribe((data:any) => {
			if (data.batteries_utilization_settings && Object.keys(data.batteries_utilization_settings).length) {
				this.batteryUtilizationSettings = data.batteries_utilization_settings;
				this.selectedDays = data.batteries_utilization_working_days;
				return;
			}
		});
		const highTemp = this.batteryUtilizationSettings.high_temperature_limit;
		const lowTemp = this.batteryUtilizationSettings.low_temperature_limit;
		this.highTemp = this.roundTemp(this.batteryUtilizationSettings.temperature_unit === SiteTemperatureUnit.CELSIUS ? this.commonService.fahToCel(highTemp) : highTemp);
		this.lowTemp = this.roundTemp(this.batteryUtilizationSettings.temperature_unit === SiteTemperatureUnit.CELSIUS ? this.commonService.fahToCel(lowTemp) : lowTemp);

		// Check if site has Lithium BATTlink devices
		this.checkForLithiumDevices();
	}

	checkForLithiumDevices() {
		this.deviceService.getSiteDevicesListing(this.siteId).subscribe((response: any) => {
			const devices = (response?.devices || []).map(device => this.commonService.decompress(device, 'deviceListAPI'));
			if (devices && devices.length) {
				this.hasLithiumDevices = devices.every(device => {
					return this.deviceTypeService.isLithiumBattLink(device);
				});
			}
		});
	}

	saveBatteryUtilizationSettings() {
		let invalidInputs = this.elementRef.nativeElement.querySelectorAll('.invalid-input');
		invalidInputs.forEach((item) => {
			item.classList.remove('invalid-input');
		});

		this.siteService.saveSiteUtilizationSettings(this.siteId, this.settingType, this.batteryUtilizationSettings, this.selectedDays).subscribe((res: any) => {
			if (res && res?.length) {
				return this.notificationService.setMessage('globalSuccessMsg');
			} else {
				return this.notificationService.setMessage('globalErrMsg');
			}
		});
	}

	onHighTempInputChange(value: number) {
		if (this.batteryUtilizationSettings.temperature_unit === SiteTemperatureUnit.CELSIUS) {
			this.batteryUtilizationSettings.high_temperature_limit = this.roundTemp(this.commonService.fahToCel(value, true));
		} else {
			this.batteryUtilizationSettings.high_temperature_limit = this.roundTemp(value);
		}
	}

	onLowTempInputChange(value: number) {
		if (this.batteryUtilizationSettings.temperature_unit === SiteTemperatureUnit.CELSIUS) {
			this.batteryUtilizationSettings.low_temperature_limit = this.roundTemp(this.commonService.fahToCel(value, true));
		} else {
			this.batteryUtilizationSettings.low_temperature_limit = this.roundTemp(value);
		}
	}
	
	validateInputs() {
		this.disableButton = false;

		for (const field in this.batteryUtilizationSettings) {
			const newValue = this.batteryUtilizationSettings[field];
			const ele = this.elementRef.nativeElement.querySelectorAll(`[name=${field}]`);
			if (ele && ele[0]) {
				this.render.removeClass(ele[0], 'invalid-input');
			}

			if (newValue == null || newValue == undefined || isNaN(newValue)) {
				this.handleInvalidInputs(field);
				continue;
			}

			switch(field) {
				case 'min_daily_battery_operating_minutes':
					if (newValue < 0 || newValue % 15 !== 0) 
						this.handleInvalidInputs(field);
				break;
				case 'min_daily_battery_utilization':
				case 'min_battery_energy_utilization':
				case 'min_daily_battery_soc':
					if (newValue < 0 || newValue > 100)
						this.handleInvalidInputs(field);
				break;
				case 'min_battery_ah_return':
					if (newValue < 75 || newValue > 100)
						this.handleInvalidInputs(field);
				break;
				case 'max_battery_low_water_level_days':
				case 'max_missed_weekly_wq':
				case 'weak_cell_voltage_limit':
				case 'no_of_inuse_cycles_with_vpc_less_weak_voltage_limit':
				case 'no_of_inuse_cycles_with_vpc_less_deep_discharge_limit':
				case'min_finish_charge_voltage_limit':
				case 'min_charge_event_duration':
				case 'deep_discharge_voltage_limit':
				case 'maximum_low_temperature_days':
				case 'maximum_high_temperature_days':
				case 'no_of_charge_cycles_with_vpc_less_min_finish_charge_voltage_limit':
				case 'min_daily_plug_time':
				case 'min_daily_plug_ins':
					if (newValue < 1)
						this.handleInvalidInputs(field);
				break;
				case 'low_temperature_limit':
				case 'high_temperature_limit':
					if (!newValue)
						this.handleInvalidInputs(field);
				break;
				case 'geofence_distance':
					if (newValue < 1)
						this.handleInvalidInputs(field);
				break;
			}
		}
	}

	isChecked(dayId: number) {
		return this.selectedDays.includes(dayId);
	}

	onCheckboxChange(value: number, isChecked: boolean) {
		this.selectedDays = isChecked ? [...this.selectedDays, value] : this.selectedDays.filter(day => day !== value);
		this.disableButton = this.selectedDays.length == 0 ? true : false;
	}

	handleInvalidInputs(field: string): boolean {
		const form = this.elementRef.nativeElement;
		const ele = form.querySelectorAll(`[name=${field}]`);
		if(ele && ele[0])
			this.render.addClass(ele[0], 'invalid-input');
		return this.disableButton = true;
	}

	roundTemp(value: number, decimals: number = 0): number {
		if (value === null || value === undefined) return value;
		const factor = Math.pow(10, decimals);
		return Math.round(value * factor) / factor;
	}

	ngOnDestroy() {
		this.subSite.unsubscribe();
	}
}
